import { Box, createStyles, Grid, Link, makeStyles, Theme, Typography } from '@material-ui/core';
import { Dispute, PaymentRequest } from '../gql-types.generated';
import React from 'react';
import { disputeReasonDisplay } from '../features/challenge-dispute/ChallengeDispute';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogContent: {
      padding: 0,
    },
    dataGridItem: {
      padding: '10px !important',
    },
    dataLabel: {
      color: 'grey',
    },
    dataValue: {
      color: 'black',
    },
    disputed: {
      color: theme.palette.error.main,
    },
    infoSections: {
      margin: 0,
      padding: theme.spacing(1.5, 3),
    },
    sectionTitles: {
      fontWeight: 'bold',
      fontSize: '17px',
      marginBottom: theme.spacing(2),
    },
    link: {
      display: 'flex',
      alignItems: 'center',
    },
    openInNew: {
      marginLeft: theme.spacing(1),
    },
    hidden: {
      display: 'none',
    },
  }),
);

const disputeResolutionDisplay = [
  { name: 'WON', display: 'Won' },
  { name: 'LOST', display: 'Lost' },
  { name: 'PENDING', display: 'Pending' },
];

interface DisputeInformationProps {
  disputedRecord: Dispute;
  relatedPayment: PaymentRequest | null | undefined;
  disputeState: string;
}

const DisputeInformation: React.FC<DisputeInformationProps> = props => {
  const classes = useStyles();
  const { disputedRecord, relatedPayment, disputeState } = props;
  if (disputeState === 'UNKNOWN') {
    return <></>;
  }
  const reason = disputeReasonDisplay.find(item => {
    return item.name === disputedRecord.reason;
  })?.display;
  const fullReason = `${reason}. ${disputedRecord.reasonMessage}`;

  const disputeResolution = disputeResolutionDisplay.find(item => {
    return item.name === disputedRecord.resolution;
  })?.display;

  const resolutionString = disputeResolution ? `${disputeResolution}` : 'Pending';

  return (
    <Box className={classes.dialogContent} data-cy="dispute-information">
      <Box className={classes.infoSections}>
        <Typography className={classes.sectionTitles}>Dispute information</Typography>
        <Grid container direction="row" spacing={2} alignItems="stretch">
          <Grid item xs={6} sm={4}>
            <Typography className={classes.dataLabel}>Dispute ID</Typography>
          </Grid>
          <Grid item xs={6} sm={8}>
            <Typography className={classes.dataValue}>{disputedRecord.id}</Typography>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Typography className={classes.dataLabel}>{disputeState === 'DISPUTED' ? 'Chargeback Code' : 'Resolution'}</Typography>
          </Grid>
          {disputeState === 'CONCEDED' ? (
            <Grid item xs={6} sm={8}>
              <Typography className={classes.dataValue}>Conceded</Typography>
              <Typography className={classes.dataValue}>{disputedRecord.concedeMessage}</Typography>
            </Grid>
          ) : (
            <Grid item xs={6} sm={8}>
              {disputeState === 'DISPUTED' ? (
                disputedRecord?.reasonDetails?.map((detail, index) => {
                  return (
                    <Typography key={`${detail.code}-${index}`} className={classes.dataValue}>
                      {detail.message}
                    </Typography>
                  );
                })
              ) : (
                <Typography className={classes.dataValue}>{`Challenged: ${resolutionString}`}</Typography>
              )}
            </Grid>
          )}
          <Grid item xs={6} sm={4}>
            <Typography className={classes.dataLabel}>Dispute Reason</Typography>
          </Grid>
          <Grid item xs={6} sm={8}>
            <Typography className={classes.dataValue}>{disputeState === 'DISPUTED' ? fullReason : reason}</Typography>
          </Grid>
          <Grid item xs={6} sm={4} className={disputeState === 'DISPUTED' ? '' : classes.hidden}>
            <Typography className={classes.dataLabel}>Fees</Typography>
          </Grid>
          <Grid item xs={6} sm={8} className={disputeState === 'DISPUTED' ? '' : classes.hidden}>
            <Typography className={(classes.dataValue, classes.disputed)}>
              A $25 non-refundable fee has been applied to your account
            </Typography>
          </Grid>
        </Grid>
      </Box>
      <Box className={classes.infoSections}>
        <Typography className={classes.sectionTitles}>Payment Request Information</Typography>
        {relatedPayment ? (
          <Grid container direction="row" spacing={2} alignItems="stretch">
            <Grid item xs={6} sm={4}>
              <Typography className={classes.dataLabel}>Reference Number</Typography>
            </Grid>
            <Grid item xs={6} sm={8}>
              <Typography className={classes.dataValue}>{relatedPayment.referenceNumber}</Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography className={classes.dataLabel}>Payer Contact</Typography>
            </Grid>
            <Grid item xs={6} sm={8}>
              <Typography className={classes.dataValue} style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                {relatedPayment.communications[0]?.email || relatedPayment.communications[0]?.phoneNumber}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography className={classes.dataLabel}>Payment Request Date</Typography>
            </Grid>
            <Grid item xs={6} sm={8}>
              <Typography className={classes.dataValue}>{new Date(relatedPayment.createdAt).toLocaleDateString()}</Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography className={classes.dataLabel}>Amount</Typography>
            </Grid>
            <Grid item xs={6} sm={8}>
              <Typography className={classes.dataValue}>
                {typeof disputedRecord.amount === 'number'
                  ? new Intl.NumberFormat('en', {
                      style: 'currency',
                      currency: disputedRecord.currency || 'USD',
                      currencyDisplay: 'symbol',
                    }).format(disputedRecord.amount / 100)
                  : null}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography className={classes.dataLabel}>Invoice</Typography>
            </Grid>
            <Grid item xs={6} sm={8}>
              <Typography className={classes.dataValue}>
                <Link
                  className={classes.link}
                  href={relatedPayment.invoiceLink ? relatedPayment.invoiceLink : '#'}
                  data-cy="download-dispute-invoice"
                >
                  {relatedPayment.invoiceLink ? 'Download' : 'No available attachments'}
                </Link>
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4} className={disputeState === 'DISPUTED' ? '' : classes.hidden}>
              <Typography className={classes.dataLabel}>Status</Typography>
            </Grid>
            <Grid item xs={6} sm={8} className={disputeState === 'DISPUTED' ? '' : classes.hidden}>
              <Typography className={(classes.dataValue, classes.disputed)}>Disputed</Typography>
            </Grid>
          </Grid>
        ) : (
          <Typography>Payment request information could not be found.</Typography>
        )}
      </Box>
    </Box>
  );
};

export default DisputeInformation;
